"use client";

import { useCallback } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCustomerId } from "@/contexts/customer-id-context";
import { entitiesQuery, schemaHashQuery } from "@/hooks/schema/schema-queries";

/**
 * Call after a schema change made from the panel. The backend bumps the hash before the
 * mutation returns, so fetching it picks up the new entity list. The ACO / permission rows
 * are refetched too: a schema change ships new ACOs, and without them a freshly created
 * entity renders as "no access" until a reload.
 *
 * Awaited so a caller can navigate straight after without the old schema flashing back in.
 */
export const refreshSchemaCache = async (queryClient: QueryClient, customerId: string | null) => {
  // A focus or interval refetch still in flight was sent before the save and would return the
  // old hash; fetchQuery would reuse it instead of asking again.
  await queryClient.cancelQueries({ queryKey: schemaHashQuery(customerId).queryKey });
  const schemaHash = await queryClient.fetchQuery(schemaHashQuery(customerId));
  await Promise.all([
    queryClient.fetchQuery(entitiesQuery(customerId, schemaHash)),
    // "myPermissions" and "acos" both live under this prefix
    queryClient.refetchQueries({ queryKey: ["permissions"] }),
  ]);
};

export const useInvalidateSchemaCache = () => {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();

  return useCallback(() => refreshSchemaCache(queryClient, customerId), [queryClient, customerId]);
};
